/**
 * Quiz Progress Screen
 * Shows who has played the quiz, their scores, and completion status per team
 */

import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  RefreshControl,
  ActivityIndicator,
  TouchableOpacity,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { format } from "date-fns";
import { nl } from "date-fns/locale";

/**
 * Quiz data
 */
interface Quiz {
  id: string;
  name: string;
  icon: string;
  color: string;
  question_count: number;
  points_per_correct: number;
  bonus_speed: number;
  is_team_based: boolean;
  max_plays_per_team: number;
  max_plays_per_user: number;
}

/**
 * Team with completion status
 */
interface TeamProgress {
  id: string;
  name: string;
  color: string;
  sessions: {
    id: string;
    userId: string;
    userName: string;
    score: number;
    correctAnswers: number;
    totalQuestions: number;
    completedAt: string;
  }[];
  totalScore: number;
  hasPlayed: boolean;
}

/**
 * Individual player stats (for non-team quizzes)
 */
interface PlayerProgress {
  userId: string;
  userName: string;
  teamName: string;
  teamColor: string;
  sessions: {
    id: string;
    score: number;
    correctAnswers: number;
    totalQuestions: number;
    completedAt: string;
  }[];
  bestScore: number;
}

export default function QuizProgressScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { activeWeekend } = useAuth();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [teamProgress, setTeamProgress] = useState<TeamProgress[]>([]);
  const [playerProgress, setPlayerProgress] = useState<PlayerProgress[]>([]);

  /**
   * Fetch quiz and progress data
   */
  const fetchData = useCallback(async () => {
    if (!id || !activeWeekend) return;

    try {
      // Fetch quiz
      const { data: quizData, error: quizError } = await supabase
        .from("quizzes")
        .select("*")
        .eq("id", id)
        .single();

      if (quizError) throw quizError;
      setQuiz(quizData);

      // Fetch all teams for this weekend
      const { data: teamsData, error: teamsError } = await supabase
        .from("teams")
        .select(`
          id,
          name,
          color,
          team_members (
            user_id,
            profiles:profiles!team_members_user_id_fkey (
              id,
              full_name
            )
          )
        `)
        .eq("weekend_id", activeWeekend.id)
        .order("name");

      if (teamsError) throw teamsError;

      // Fetch all completed sessions for this quiz
      const { data: sessionsData, error: sessionsError } = await supabase
        .from("game_sessions")
        .select(`
          id,
          created_by,
          participants,
          points_awarded,
          completed_at,
          game_state,
          result,
          profiles!game_sessions_created_by_fkey (
            full_name
          )
        `)
        .eq("quiz_id", id)
        .eq("status", "completed")
        .order("completed_at", { ascending: false });

      if (sessionsError) throw sessionsError;

      // Build team progress
      const teams: TeamProgress[] = (teamsData || []).map((team: any) => {
        // Find sessions for this team
        const teamSessions = (sessionsData || []).filter((session: any) =>
          session.participants?.some((p: any) => p.team_id === team.id)
        );

        return {
          id: team.id,
          name: team.name,
          color: team.color || "#3B82F6",
          sessions: teamSessions.map((session: any) => ({
            id: session.id,
            userId: session.created_by,
            userName: session.profiles?.full_name || "Onbekend",
            score: session.points_awarded || 0,
            correctAnswers: session.result?.stats?.correct_answers || 0,
            totalQuestions: session.result?.stats?.total_questions || quizData.question_count,
            completedAt: session.completed_at,
          })),
          totalScore: teamSessions.reduce(
            (sum: number, s: any) => sum + (s.points_awarded || 0),
            0
          ),
          hasPlayed: teamSessions.length > 0,
        };
      });

      // Sort by total score (highest first)
      teams.sort((a, b) => b.totalScore - a.totalScore);
      setTeamProgress(teams);

      // Build player progress (for non-team quizzes)
      if (!quizData.is_team_based) {
        const playerMap = new Map<string, PlayerProgress>();

        (sessionsData || []).forEach((session: any) => {
          const participant = session.participants?.[0];
          const team = (teamsData || []).find((t: any) => t.id === participant?.team_id);
          
          if (!playerMap.has(session.created_by)) {
            playerMap.set(session.created_by, {
              userId: session.created_by,
              userName: session.profiles?.full_name || "Onbekend",
              teamName: team?.name || "Geen team",
              teamColor: team?.color || "#6B7280",
              sessions: [],
              bestScore: 0,
            });
          }

          const player = playerMap.get(session.created_by)!;
          player.sessions.push({
            id: session.id,
            score: session.points_awarded || 0,
            correctAnswers: session.result?.stats?.correct_answers || 0,
            totalQuestions: session.result?.stats?.total_questions || quizData.question_count,
            completedAt: session.completed_at,
          });
          player.bestScore = Math.max(player.bestScore, session.points_awarded || 0);
        });

        const players = Array.from(playerMap.values());
        players.sort((a, b) => b.bestScore - a.bestScore);
        setPlayerProgress(players);
      }
    } catch (error) {
      console.error("Error fetching quiz progress:", error);
    } finally {
      setIsLoading(false);
    }
  }, [id, activeWeekend]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  }, [fetchData]);

  const maxPoints = quiz
    ? quiz.question_count * (quiz.points_per_correct + quiz.bonus_speed)
    : 0;

  const completedTeams = teamProgress.filter((t) => t.hasPlayed).length;
  const totalTeams = teamProgress.length;

  if (isLoading) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={["top", "bottom"]}>
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </SafeAreaView>
    );
  }

  if (!quiz) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={["top", "bottom"]}>
        <View className="flex-1 items-center justify-center p-6">
          <Ionicons name="alert-circle" size={48} color={colors.textTertiary} />
          <Text className="mt-4" style={{ color: colors.textSecondary }}>
            Quiz niet gevonden
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={["top", "bottom"]}>
      {/* Header */}
      <View
        className="px-4 py-3 flex-row items-center"
        style={{
          backgroundColor: colors.surface,
          borderBottomWidth: 1,
          borderBottomColor: colors.border,
        }}
      >
        <TouchableOpacity
          onPress={() => router.back()}
          className="w-10 h-10 rounded-full items-center justify-center mr-3"
          style={{ backgroundColor: colors.surfaceSecondary }}
        >
          <Ionicons name="arrow-back" size={22} color={colors.text} />
        </TouchableOpacity>
        <View className="flex-1">
          <Text className="text-lg font-bold" style={{ color: colors.text }}>
            Voortgang
          </Text>
          <Text className="text-sm" style={{ color: colors.textSecondary }}>
            {quiz.name}
          </Text>
        </View>
        <TouchableOpacity
          onPress={handleRefresh}
          className="w-10 h-10 rounded-full items-center justify-center"
          style={{ backgroundColor: colors.surfaceSecondary }}
        >
          <Ionicons name="refresh" size={20} color={colors.text} />
        </TouchableOpacity>
      </View>

      <ScrollView
        className="flex-1"
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            colors={[colors.primary]}
            tintColor={colors.primary}
          />
        }
        contentContainerStyle={{ paddingBottom: 40 }}
      >
        {/* Summary Stats */}
        <View className="px-4 pt-4">
          <View
            className="rounded-xl p-4"
            style={{
              backgroundColor: `${quiz.color}15`,
              borderWidth: 2,
              borderColor: quiz.color,
            }}
          >
            <View className="flex-row items-center mb-3">
              <View
                className="w-12 h-12 rounded-xl items-center justify-center mr-3"
                style={{ backgroundColor: `${quiz.color}25` }}
              >
                <Ionicons name={quiz.icon as any} size={24} color={quiz.color} />
              </View>
              <View className="flex-1">
                <Text className="text-lg font-bold" style={{ color: colors.text }}>
                  {quiz.name}
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  {quiz.is_team_based ? "Team Quiz" : "Individuele Quiz"}
                </Text>
              </View>
            </View>

            <View className="flex-row flex-wrap">
              <View className="w-1/3 pr-2">
                <View
                  className="rounded-lg p-3 items-center"
                  style={{ backgroundColor: colors.background }}
                >
                  <Text className="text-2xl font-bold" style={{ color: quiz.color }}>
                    {completedTeams}/{totalTeams}
                  </Text>
                  <Text className="text-xs" style={{ color: colors.textSecondary }}>
                    Teams gespeeld
                  </Text>
                </View>
              </View>
              <View className="w-1/3 px-1">
                <View
                  className="rounded-lg p-3 items-center"
                  style={{ backgroundColor: colors.background }}
                >
                  <Text className="text-2xl font-bold" style={{ color: "#10B981" }}>
                    {teamProgress.reduce((sum, t) => sum + t.sessions.length, 0)}
                  </Text>
                  <Text className="text-xs" style={{ color: colors.textSecondary }}>
                    Sessies
                  </Text>
                </View>
              </View>
              <View className="w-1/3 pl-2">
                <View
                  className="rounded-lg p-3 items-center"
                  style={{ backgroundColor: colors.background }}
                >
                  <Text className="text-2xl font-bold" style={{ color: "#F59E0B" }}>
                    {maxPoints}
                  </Text>
                  <Text className="text-xs" style={{ color: colors.textSecondary }}>
                    Max punten
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* Progress by Team */}
        <View className="px-4 mt-6">
          <Text className="text-lg font-semibold mb-3" style={{ color: colors.text }}>
            Voortgang per Team
          </Text>

          {teamProgress.map((team) => (
            <View
              key={team.id}
              className="rounded-xl mb-3 overflow-hidden"
              style={{
                backgroundColor: colors.surface,
                borderWidth: 1,
                borderColor: team.hasPlayed ? team.color : colors.border,
              }}
            >
              {/* Team Header */}
              <View
                className="flex-row items-center justify-between p-4"
                style={{
                  backgroundColor: team.hasPlayed ? `${team.color}10` : "transparent",
                }}
              >
                <View className="flex-row items-center flex-1">
                  <View
                    className="w-10 h-10 rounded-full items-center justify-center mr-3"
                    style={{ backgroundColor: `${team.color}25` }}
                  >
                    <Text className="text-lg font-bold" style={{ color: team.color }}>
                      {team.name.charAt(0)}
                    </Text>
                  </View>
                  <View className="flex-1">
                    <Text className="font-semibold" style={{ color: colors.text }}>
                      {team.name}
                    </Text>
                    {team.hasPlayed ? (
                      <Text className="text-xs" style={{ color: colors.textSecondary }}>
                        {team.sessions.length} poging(en)
                      </Text>
                    ) : (
                      <Text className="text-xs" style={{ color: colors.textTertiary }}>
                        Nog niet gespeeld
                      </Text>
                    )}
                  </View>
                </View>

                {team.hasPlayed ? (
                  <View className="items-end">
                    <View className="flex-row items-center">
                      <Ionicons name="star" size={16} color="#F59E0B" />
                      <Text className="ml-1 font-bold text-lg" style={{ color: "#F59E0B" }}>
                        {team.totalScore}
                      </Text>
                    </View>
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>
                      punten
                    </Text>
                  </View>
                ) : (
                  <View
                    className="px-3 py-1 rounded-full"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Text className="text-xs" style={{ color: colors.textTertiary }}>
                      Wacht...
                    </Text>
                  </View>
                )}
              </View>

              {/* Session Details */}
              {team.sessions.length > 0 && (
                <View
                  className="px-4 pb-3 pt-2"
                  style={{ borderTopWidth: 1, borderTopColor: colors.border }}
                >
                  {team.sessions.map((session, idx) => (
                    <View
                      key={session.id}
                      className="py-2"
                      style={{
                        borderBottomWidth: idx < team.sessions.length - 1 ? 1 : 0,
                        borderBottomColor: colors.border,
                      }}
                    >
                      <View className="flex-row items-center justify-between">
                        <View className="flex-row items-center flex-1">
                          <Ionicons name="person" size={14} color={colors.textSecondary} />
                          <Text className="ml-2" style={{ color: colors.text }}>
                            {session.userName}
                          </Text>
                        </View>
                        <View className="flex-row items-center">
                          <Text className="text-sm mr-3" style={{ color: colors.textSecondary }}>
                            {session.correctAnswers}/{session.totalQuestions}
                          </Text>
                          <Text className="font-bold" style={{ color: "#F59E0B" }}>
                            {session.score} pts
                          </Text>
                        </View>
                      </View>
                      {session.completedAt && (
                        <View className="flex-row items-center mt-1 ml-5">
                          <Ionicons name="time-outline" size={12} color={colors.textTertiary} />
                          <Text className="ml-1 text-xs" style={{ color: colors.textTertiary }}>
                            {format(new Date(session.completedAt), "d MMM HH:mm", { locale: nl })}
                          </Text>
                        </View>
                      )}
                    </View>
                  ))}
                </View>
              )}
            </View>
          ))}
        </View>

        {/* Leaderboard for non-team quizzes */}
        {!quiz.is_team_based && playerProgress.length > 0 && (
          <View className="px-4 mt-6">
            <Text className="text-lg font-semibold mb-3" style={{ color: colors.text }}>
              Ranglijst Spelers
            </Text>

            {playerProgress.map((player, index) => (
              <View
                key={player.userId}
                className="rounded-xl mb-2 p-3 flex-row items-center"
                style={{
                  backgroundColor: colors.surface,
                  borderWidth: 1,
                  borderColor: index < 3 ? ["#F59E0B", "#9CA3AF", "#CD7F32"][index] : colors.border,
                }}
              >
                <View
                  className="w-8 h-8 rounded-full items-center justify-center mr-3"
                  style={{
                    backgroundColor:
                      index < 3
                        ? [`#F59E0B20`, `#9CA3AF20`, `#CD7F3220`][index]
                        : colors.surfaceSecondary,
                  }}
                >
                  <Text
                    className="font-bold"
                    style={{
                      color:
                        index < 3
                          ? ["#F59E0B", "#9CA3AF", "#CD7F32"][index]
                          : colors.textSecondary,
                    }}
                  >
                    {index + 1}
                  </Text>
                </View>
                <View className="flex-1">
                  <Text className="font-medium" style={{ color: colors.text }}>
                    {player.userName}
                  </Text>
                  <View className="flex-row items-center flex-wrap">
                    <View
                      className="w-2 h-2 rounded-full mr-1"
                      style={{ backgroundColor: player.teamColor }}
                    />
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>
                      {player.teamName} • {player.sessions.length} poging(en)
                    </Text>
                  </View>
                  {player.sessions[0]?.completedAt && (
                    <View className="flex-row items-center mt-0.5">
                      <Ionicons name="time-outline" size={10} color={colors.textTertiary} />
                      <Text className="ml-1 text-xs" style={{ color: colors.textTertiary }}>
                        {format(new Date(player.sessions[0].completedAt), "d MMM HH:mm", { locale: nl })}
                      </Text>
                    </View>
                  )}
                </View>
                <View className="items-end">
                  <Text className="font-bold text-lg" style={{ color: "#F59E0B" }}>
                    {player.bestScore}
                  </Text>
                  <Text className="text-xs" style={{ color: colors.textSecondary }}>
                    beste score
                  </Text>
                </View>
              </View>
            ))}
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

